<template>
  <div class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loadingData && !loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-form v-if="!loading">
      <v-row>
        <v-col cols="12" md="12" sm="12">
          <v-card class="pa-5"  :style="{ border: '1px solid var(--primary-boarder-color)' }">
            <HeaderDividerBlock
              headerText="Project Stakeholders"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" md="4" sm="12">
                <v-autocomplete
                  v-model="form.customer"
                  :items="formattedCustomers"
                  item-value="uuid"
                  outlined
                  dense
                  clearable
                  :search-input.sync="search"
                  @update:search-input="searchChange"
                  :error-messages="this.fieldErrors.customer_id"
                  :label="customerLabel"
                >
                </v-autocomplete>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-autocomplete
                  v-model="form.manager"
                  :items="managers"
                  :item-text="formatMemberText"
                  :item-value="'uuid'"
                  label="Project Manager*"
                  dense
                  outlined
                  clearable
                  :error-messages="this.fieldErrors.manager_id"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-autocomplete
                  v-model="form.member"
                  :items="members"
                  :item-text="formatMemberText"
                  :item-value="'uuid'"
                  label="Members"
                  dense
                  outlined
                  multiple
                  clearable
                  :error-messages="this.fieldErrors.member_id"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card>
          <v-card class="pa-5 my-5"   :style="{ border: '1px solid var(--primary-boarder-color)' }">
            <HeaderDividerBlock
              headerText="Project Details"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" md="6" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.project_name"
                  label="Project Name*"
                  required
                  :error-messages="this.fieldErrors.project_name"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="6" sm="12">
                <v-textarea
                  rows="1"
                  outlined
                  dense
                  v-model="form.project_description"
                  label="Project Description*"
                  :error-messages="this.fieldErrors.project_description"
                >
                </v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" md="3" sm="12">
                <v-autocomplete
                  v-model="form.project_type"
                  :items="projectTypes"
                  :item-text="'name'"
                  :item-value="'uuid'"
                  label="Project Type*"
                  dense
                  outlined
                  @change="typeChange()"
                  clearable
                  :error-messages="this.fieldErrors.project_type"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <v-select
                  v-model="form.priority"
                  :items="priority"
                  label="Project Priority"
                  dense
                  outlined
                  @change="typeChange()"
                  :error-messages="this.fieldErrors.priority"
                ></v-select>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <DatePicker
                  :label="'Start Date'"
                  v-model="form.start_date"
                  :selectValue="form.start_date"
                  offset-y
                  @changeInput="form.start_date = $event"
                  :error="this.fieldErrors.start_date"
                  :maxDate="maxStartDate"
                >
                </DatePicker>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <DatePicker
                  :label="'Due Date'"
                  v-model="form.due_date"
                  :selectValue="form.due_date"
                  offset-y
                  @changeInput="form.due_date = $event"
                  :error="this.fieldErrors.due_date"
                  :minDate="minDueDate"
                >
                </DatePicker>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" md="3" sm="12">
                <v-autocomplete
                  v-model="form.project_branch"
                  :items="projectBranches"
                  :item-text="'name'"
                  :item-value="'uuid'"
                  label="Project Branch*"
                  dense
                  outlined
                  @change="typeChange()"
                  clearable
                  :error-messages="this.fieldErrors.project_branch"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="6" sm="12">
                <v-row>
                  <v-col cols="8">
                    <div @click="handleTextareaClick">
                      <v-textarea
                        outlined
                        dense
                        rows="1"
                        v-model="form.project_location"
                        label="Project Location"
                        @input="handleLocationChange"
                        :error-messages="this.fieldErrors.project_location"
                      >
                      </v-textarea>
                    </div>
                  </v-col>
                  <v-col cols="4" class="ma-a pa-0">
                    <v-checkbox
                      v-model="form.customerLocation"
                      @change="toggleCustomerLocation"
                      label="Customer Location"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <v-textarea
                  outlined
                  dense
                  rows="1"
                  v-model="form.project_location_link"
                  label="Location Map Link"
                  :error-messages="this.fieldErrors.location_map_url"
                >
                </v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" md="3" sm="12">
                <v-select
                  v-model="form.status"
                  :items="status"
                  label="Project Status"
                  dense
                  outlined
                  @change="typeChangeStatus()"
                  :error-messages="this.fieldErrors.status"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>
          <v-card class="pa-5"   :style="{ border: '1px solid var(--primary-boarder-color)' }">
            <HeaderDividerBlock
              headerText="Project Parameters"
            ></HeaderDividerBlock>

            <v-row>
              <v-col cols="6" md="4" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.gross_floor_area"
                  label="Gross Floor Area (m²)"
                  :error-messages="this.fieldErrors.gross_floor_area"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.build_up_area"
                  label="Built Up Area (m²)"
                  required
                  :error-messages="this.fieldErrors.build_up_area"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.budget"
                  :label="`Budget (${currencyCode})*`"
                  required
                  :error-messages="this.fieldErrors.budget"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-card-actions v-if="!cpRole">
              <v-row>
                <v-col cols="12" md="9" xs="6"></v-col>
                <v-col cols="12" md="3" xs="6">
                  <v-btn
                    large
                    color="#00B3FF"
                    dark
                    depressed
                    block
                    @click="submit()"
                    >Update Project</v-btn
                  >
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <Snackbars
      :text="succesMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="clossSnackbar"
    />
  </div>
</template>
<script>
import DatePicker from "@/components/shared/DatePicker";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import { ProjectService } from "../../services/project-service.js";
import { checkError } from "@/config/errors.js";
import { CustomerService } from "@/services/customer-service.js";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import debounce from "lodash.debounce";
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");

export default {
  name: "Editproject",

  components: { Alert, Snackbars, DatePicker, HeaderDividerBlock },
  props: ["uuid"],
  data: () => ({
    cpRole: false,
    showCompletedCheckbox: false,
    loading: false,
    loadingData: false,
    customerLocation: false,
    errors: null,
    customer_address: "",
    customer_city: "",
    customer_state: "",
    customer_country: "",
    page: 1,
    search: " ",
    searchUser: " ",
    searchManagerUser: " ",
    isCheckboxDisabled: false,
    isStatusCompleted: false,
    snackbar: false,
    isChecked: false,
    succesMsg: "Project Updated Successfully",
    form: {
      project_name: "",
      customer: "",
      project_description: "",
      start_date: "",
      due_date: "",
      priority: "",
      member: [],
      manager: "",
      project_location: "",
      project_location_link: "",
      uuidArray: [],
      gross_floor_area: "",
      build_up_area: "",
      project_type: "",
      project_branch: "",
      budget: "",
      customerLocation: false,
    },
    role: localStorage.getItem("role") || null,
    projects: [],
    customers: [],
    managers: [],
    members: [],
    projectTypes: [],
    projectBranches: [],

    priority: ["LOW", "MEDIUM", "HIGH"],

    project_types: ["Residential", "Hotel", "Villa"],

    status: ["PENDING", "IN-PROGRESS", "HOLD", "COMPLETED", "PLANNING"],
    fieldErrors: [],
    minDueDate: '',
    maxStartDate: '',
  }),

  mounted() {
    if (this.$route.params.uuid) {
      this.onDismissed();
    }
    this.getCustomersList();
    this.getMembersList();
    this.getProjectInfo();
    this.getProjectTypes();
    this.getProjectBranches();
  },

  watch: {
    search() {
      this.page = 1;
      this.getCustomersList();
    },
    searchUser() {
      this.page = 1;
      this.getMembersList();
    },
    searchManagerUser() {
      this.page = 1;
      this.getMembersList();
    },
    'form.start_date'(newVal) {
      this.minDueDate = newVal;
    },
    'form.due_date'(newVal) {
      this.maxStartDate = newVal;
    },
  },

  computed: {
    ...mapGetters({
      member: "profile",
    }),

    ...mapSettingsGetters({
      currencyCode: "currencyCode",
    }),

    formattedCustomers() {
      return this.customers.map((customer) => ({
        ...customer,
        text: `${customer.full_name} - ${customer.code}`,
        uuid: customer.uuid,
      }));
    },
    customerLabel() {
      return this.form.customer ? "Customer*" : "Search Customer";
    },
  },

  methods: {
    submit() {
      this.errors = null;
      this.loading = true;

      let fd = new FormData();

      fd.append("project_name", this.form.project_name);
      fd.append("gross_floor_area", this.form.gross_floor_area);
      fd.append("build_up_area", this.form.build_up_area);
      fd.append("budget", this.form.budget);
      fd.append("customer_id", this.form.customer);
      fd.append("start_date", this.form.start_date);
      fd.append("due_date", this.form.due_date);
      fd.append("project_description", this.form.project_description);
      fd.append("priority", this.form.priority);
      fd.append("status", this.form.status);

      if (Array.isArray(this.form.member) && this.form.member.length > 0) {
        if (this.form.member.every((obj) => typeof obj === "object")) {
          this.uuidArray = this.form.member.map((obj) => obj.uuid);
          fd.append("member_id", this.uuidArray.join(","));
        } else {
          fd.append("member_id", this.form.member.join(","));
        }
      } else {
        fd.append("member_id", "");
      }

      if (this.form.manager && typeof this.form.manager === "object") {
        fd.append("manager_id", this.form.manager.uuid);
      } else if (this.form.manager) {
        fd.append("manager_id", this.form.manager);
      }

      if (
        this.form.project_type &&
        typeof this.form.project_type === "object"
      ) {
        fd.append("project_type", this.form.project_type.uuid);
      } else if (this.form.project_type) {
        fd.append("project_type", this.form.project_type);
      }

      if (
        this.form.project_branch &&
        typeof this.form.project_branch === "object"
      ) {
        fd.append("project_branch", this.form.project_branch.uuid);
      } else if (this.form.project_branch) {
        fd.append("project_branch", this.form.project_branch);
      }

      fd.append("project_location", this.form.project_location);
      fd.append("location_map_url", this.form.project_location_link);
      fd.append("is_customer_location", this.form.customerLocation ? 1 : 0);

      ProjectService.projectUpdate(this.$route.params.uuid, fd)
        .then((response) => {
          this.loading = false;
          if (response.data) {
            this.snackbar = true;
            this.snackbarError = false;
            this.fieldErrors = [];
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.fieldErrors = data.errors;
          this.errors = errorOutput;
        });
    },

    getProjectInfo() {
      this.loading = true;
      ProjectService.getProjectInfo(this.$route.params.uuid)
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            const projectData = response.data.data;
            this.setDefaultFormValues(projectData);
            if (projectData.invoice_id !== null) {
              this.isStatusCompleted = true;
            }
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    toggleCustomerLocation() {
      if (this.form.customerLocation) {
        this.getCustomerAddress();
      } else {
        this.form.project_location = "";
      }
    },

    getCustomerAddress() {
      this.loadingData = true;
      CustomerService.getProjectCustomerAddress(this.form.customer)
        .then((response) => {
          this.loadingData = false;
          if (response.data.data) {
            const customerData = response.data.data;
            this.customer_address = customerData.address1;
            this.customer_city = customerData.city;
            this.customer_state = customerData.state;
            this.customer_country = customerData.country;
            this.form.project_location =
              this.customer_address +
              ", " +
              this.customer_city +
              ", " +
              this.customer_state +
              ", " +
              this.customer_country;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    typeChangeStatus() {
      this.showCompletedCheckbox = this.form.status === "COMPLETED";
    },

    handleTextareaClick() {
      this.customerLocation = false;
    },

    handleLocationChange() {
      if (
        this.form.project_location !==
        this.customer_address +
          ", " +
          this.customer_city +
          ", " +
          this.customer_state +
          ", " +
          this.customer_country
      ) {
        this.form.project_location = "";
        this.form.customerLocation = false;
      }
    },

    setDefaultFormValues(projectData) {
      this.form = {
        ...this.form,
        project_name: projectData.project_name || "",
        status: projectData.status || "",
        customer: projectData.customer.uuid || "",
        start_date: projectData.start_date || "",
        due_date: projectData.due_date || "",
        project_description: projectData.project_description || "",
        priority: projectData.priority || "",
        manager: projectData.manager ? projectData.manager.uuid : "",
        member: projectData.members
          ? projectData.members.map((member) => member.uuid)
          : [],
        project_location: projectData.project_location || "",
        project_location_link: projectData.location_map_url || "",
        gross_floor_area: projectData.gross_floor_area || "",
        build_up_area: projectData.build_up_area || "",
        project_type: projectData.project_type || "",
        project_branch: projectData.project_branch || "",
        budget: projectData.budget || "",
        customerLocation: projectData.is_customer_location || 0,
      };
    },

    getCustomersList: debounce(function() {
      this.loadingData = true;
      CustomerService.getCustomerList(this.search ?? "", this.page)
        .then((response) => {
          this.loadingData = false;
          if (response.data.data) {
            this.customers = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalCustomer = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.customers = [];
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    }, 1000),

    getMembersList() {
      this.loading = true;
      ProjectService.getUsersList()
        .then((response) => {
          this.loading = false;
          if (response.data) {
            this.managers = response.data.data;
            this.members = response.data.data;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    getProjectTypes() {
      this.loading = true;
      ProjectService.getProjectTypes()
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.projectTypes = response.data.data;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    getProjectBranches() {
      this.loading = true;
      ProjectService.getProjectBranches()
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.projectBranches = response.data.data;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    onDismissed() {
      this.errors = null;
    },
    clossSnackbar() {
      this.snackbar = false;
    },
    reset() {
      this.form = [];
    },
    searchChange(searchText) {
      if (searchText) {
        const parts = searchText.split(" -");
        this.search = parts[0];
      } else {
        this.search = "";
      }
      this.page = 1;
      this.getCustomersList();
    },
    searchUserChange(searchText) {
      this.searchUser = searchText;
    },
    searchManagerUserChange(searchText) {
      this.searchManagerUser = searchText;
    },
    formatMemberText(member) {
      if (member.employer && member.employer.speciality) {
        return `${member.employer.full_name} - ${member.employer.speciality.name}`;
      } else {
        return member.employer.full_name;
      }
    },
  },
};
</script>
